import FilterAltOffRounded from '@mui/icons-material/FilterAltOffRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Collapse,
  IconButton,
  Link,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import type { PlatformAdmin, PlatformAuditEntry } from '../../api/m19';
import { useAuth } from '../../auth/AuthProvider';
import { EmptyState, ErrorState, PageHeader } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { Field, JsonBlock } from '../../components/ui';
import { dateTime, relativeTime } from '../../lib/format';

/** Actions the platform writes today (the filter also accepts any other value). */
export const KNOWN_ACTIONS = [
  'platform.login',
  'platform.logout',
  'platform.mfa_challenge',
  'platform.mfa_setup',
  'platform.mfa_enable',
  'platform.mfa_disable',
  'platform.password_change',
  'platform.refresh_reuse_detected',
  'platform_admin.create',
  'platform_admin.update',
  'platform_admin.reset_mfa',
  'permission.denied',
  'tenant.view',
  'tenant.menus',
  'tenant.backup',
  'tenant.user_password_reset',
  'tenant.suspend',
  'tenant.resume',
  'subscription.update',
  'subscription.extend',
  'plan.create',
  'plan.update',
  'activation_code.generate',
  'activation_code.revoke',
  'activation_code.deliver',
  'support.access',
];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const mono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/** `YYYY-MM-DD` (admin's local day) → ISO instant of that local midnight, plus `days`. */
export function dayStart(day: string, days = 0): string | undefined {
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d + days).toISOString();
}

interface Filters {
  action: string;
  adminId: string;
  tenantId: string;
  from: string;
  to: string;
  page: number;
  pageSize: number;
}

function readFilters(p: URLSearchParams): Filters {
  const n = (k: string, d: number) => {
    const v = Number(p.get(k));
    return Number.isInteger(v) && v > 0 ? v : d;
  };
  return {
    action: p.get('action') ?? '',
    adminId: p.get('adminId') ?? '',
    tenantId: p.get('tenantId') ?? '',
    from: p.get('from') ?? '',
    to: p.get('to') ?? '',
    page: n('page', 1),
    pageSize: Math.min(100, n('pageSize', 25)),
  };
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

export function AuditPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const f = readFilters(params);
  const set = (patch: Partial<Record<keyof Filters, string | number>>, keepPage = false) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === '') next.delete(k);
      else next.set(k, String(v));
    }
    if (!keepPage) next.delete('page');
    setParams(next, { replace: true });
  };
  const filtered = Boolean(f.action || f.adminId || f.tenantId || f.from || f.to);

  const admins = useQuery({
    queryKey: ['platform-admins'],
    queryFn: () => unwrap(api.GET('/platform/admins')),
    enabled: can('admins.manage'),
    staleTime: 60_000,
  });
  const adminById = new Map<string, PlatformAdmin>((admins.data?.data ?? []).map((a) => [a.id, a]));

  const [tenantText, setTenantText] = useState('');
  const tenants = useQuery({
    queryKey: ['audit-tenant-options', tenantText],
    queryFn: () =>
      unwrap(
        api.GET('/platform/tenants', {
          params: {
            query: { pageSize: 100, ...(tenantText.trim() ? { q: tenantText.trim() } : {}) },
          },
        }),
      ),
    enabled: can('tenants.view'),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const [tenantNames, setTenantNames] = useState<Record<string, string>>({});
  const tenantOptions = tenants.data?.data ?? [];
  const tenantName = (id: string) =>
    tenantNames[id] ?? tenantOptions.find((t) => t.id === id)?.name;

  const fromIso = f.from ? dayStart(f.from) : undefined;
  const toIso = f.to ? dayStart(f.to, 1) : undefined;
  const rangeInvalid = Boolean(f.from && f.to && f.from > f.to);

  const query = useQuery({
    queryKey: ['platform-audit', f],
    queryFn: () =>
      unwrap(
        api.GET('/platform/audit-logs', {
          params: {
            query: {
              page: f.page,
              pageSize: f.pageSize,
              action: f.action || undefined,
              adminId: UUID.test(f.adminId) ? f.adminId : undefined,
              tenantId: UUID.test(f.tenantId) ? f.tenantId : undefined,
              from: fromIso,
              to: toIso,
            },
          },
        }),
      ),
    enabled: !rangeInvalid,
    placeholderData: keepPreviousData,
  });
  const rows = query.data?.data;

  const actor = (e: PlatformAuditEntry) => {
    if (!e.adminId) return { label: e.actorType === 'SYSTEM' ? 'System' : humanActor(e.actorType) };
    const a = adminById.get(e.adminId);
    return a ? { label: a.name, sub: a.email } : { label: 'Admin', sub: shortId(e.adminId) };
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Platform"
        title="Audit log"
        subtitle="Every platform action: sign-ins, tenant changes, subscriptions, codes, plans and support access."
      />

      <Card sx={{ mb: 2.5, p: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              lg: '1.3fr 1.1fr 1.3fr 0.8fr 0.8fr auto',
            },
            alignItems: 'start',
          }}
        >
          <Autocomplete
            freeSolo
            options={KNOWN_ACTIONS}
            value={f.action || null}
            onChange={(_, v) => set({ action: v ?? '' })}
            onBlur={(e) => {
              const v = (e.target as HTMLInputElement).value.trim();
              if (v !== f.action) set({ action: v });
            }}
            renderInput={(p) => (
              <TextField
                {...p}
                label="Action"
                placeholder="e.g. tenant.suspend"
                slotProps={{
                  ...p.slotProps,
                  htmlInput: { ...p.slotProps.htmlInput, style: { fontFamily: mono } },
                }}
              />
            )}
          />
          {can('admins.manage') ? (
            <TextField
              select
              label="Actor"
              value={f.adminId}
              onChange={(e) => set({ adminId: e.target.value })}
            >
              <MenuItem value="">Anyone</MenuItem>
              {(admins.data?.data ?? []).map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              label="Actor (admin ID)"
              defaultValue={f.adminId}
              onBlur={(e) => set({ adminId: e.target.value.trim() })}
              error={Boolean(f.adminId) && !UUID.test(f.adminId)}
              helperText={f.adminId && !UUID.test(f.adminId) ? 'Paste a full admin ID' : undefined}
            />
          )}
          <Autocomplete
            options={tenantOptions}
            getOptionLabel={(t) => t.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(o) => o}
            loading={tenants.isFetching}
            value={
              f.tenantId
                ? (tenantOptions.find((t) => t.id === f.tenantId) ?? {
                    ...placeholderTenant,
                    id: f.tenantId,
                    name: tenantName(f.tenantId) ?? shortId(f.tenantId),
                  })
                : null
            }
            onChange={(_, t) => {
              if (t) setTenantNames((m) => ({ ...m, [t.id]: t.name }));
              set({ tenantId: t?.id ?? '' });
            }}
            onInputChange={(_, v, reason) => {
              if (reason === 'input') setTenantText(v);
            }}
            renderOption={(props, t) => (
              <li {...props} key={t.id}>
                <Box>
                  <Typography variant="body2">{t.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.slug}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(p) => <TextField {...p} label="Shop" placeholder="Any shop" />}
          />
          <TextField
            label="From"
            type="date"
            value={f.from}
            onChange={(e) => set({ from: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
            error={rangeInvalid}
          />
          <TextField
            label="To"
            type="date"
            value={f.to}
            onChange={(e) => set({ to: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
            error={rangeInvalid}
            helperText={rangeInvalid ? 'Ends before “From”' : undefined}
          />
          <Button
            variant="text"
            startIcon={<FilterAltOffRounded />}
            disabled={!filtered}
            onClick={() => {
              setTenantText('');
              setParams(new URLSearchParams(), { replace: true });
            }}
            sx={{ height: 40, whiteSpace: 'nowrap' }}
          >
            Clear
          </Button>
        </Box>
      </Card>

      {query.isError ? (
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table aria-label="Audit entries">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 48, p: 0 }} />
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>When</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Actor</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Shop</TableCell>
                  <TableCell>Outcome</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(query.isPending || rangeInvalid) &&
                  !rows &&
                  Array.from({ length: 8 }, (_, i) => (
                    <TableRow key={i}>
                      {[0, 1, 2, 3, 4, 5].map((c) => (
                        <TableCell
                          key={c}
                          sx={
                            c === 1 || c === 3
                              ? { display: { xs: 'none', sm: 'table-cell' } }
                              : c === 4
                                ? { display: { xs: 'none', md: 'table-cell' } }
                                : undefined
                          }
                        >
                          {c > 0 && <Skeleton width="70%" />}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                {rows?.map((e) => (
                  <AuditRow
                    key={e.id}
                    entry={e}
                    actor={actor(e)}
                    tenantName={e.tenantId ? tenantName(e.tenantId) : undefined}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {rows?.length === 0 && (
            <EmptyState
              dense
              icon={<HistoryRounded />}
              title={filtered ? 'No entries match these filters' : 'No audit entries yet'}
              body={
                filtered
                  ? 'Try a wider date range or clear a filter. Actions must match exactly.'
                  : 'Platform actions appear here as soon as they happen.'
              }
            />
          )}
          {query.data && query.data.page.total > 0 && (
            <TablePagination
              component="div"
              count={query.data.page.total}
              page={f.page - 1}
              rowsPerPage={f.pageSize}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={(_, p) => set({ page: p + 1 }, true)}
              onRowsPerPageChange={(e) => set({ pageSize: Number(e.target.value) })}
              sx={{ borderTop: 1, borderColor: 'divider' }}
            />
          )}
        </Card>
      )}
    </Box>
  );
}

const placeholderTenant = {
  id: '',
  slug: '',
  name: '',
  status: 'ACTIVE' as const,
  createdAt: '',
  subscription: null,
  onTrial: false,
};

function humanActor(t: string): string {
  return t === 'ANONYMOUS' ? 'Anonymous' : t.replace(/_/g, ' ').toLowerCase();
}

function isBeforeAfter(
  v: unknown,
): v is { before: unknown; after: unknown } & Record<string, unknown> {
  return typeof v === 'object' && v !== null && 'before' in v && 'after' in v;
}

function AuditRow({
  entry: e,
  actor,
  tenantName,
}: {
  entry: PlatformAuditEntry;
  actor: { label: string; sub?: string };
  tenantName?: string;
}) {
  const [open, setOpen] = useState(false);
  const detailId = `audit-${e.id}`;
  const changes: unknown = e.changes;
  const extra = isBeforeAfter(changes)
    ? Object.fromEntries(Object.entries(changes).filter(([k]) => k !== 'before' && k !== 'after'))
    : null;
  return (
    <Fragment>
      <TableRow
        hover
        onClick={() => setOpen((o) => !o)}
        sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 0 : undefined } }}
      >
        <TableCell sx={{ p: 0, pl: 1 }}>
          <IconButton
            size="small"
            aria-label={open ? 'Hide details' : 'Show details'}
            aria-expanded={open}
            aria-controls={detailId}
            onClick={(ev) => {
              ev.stopPropagation();
              setOpen((o) => !o);
            }}
          >
            <KeyboardArrowDownRounded
              sx={{ transition: 'transform 150ms', transform: open ? 'rotate(180deg)' : 'none' }}
            />
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>
          <Typography variant="body2" className="tabular">
            {dateTime(e.occurredAt)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {relativeTime(e.occurredAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Box
            component="code"
            sx={{
              fontFamily: mono,
              fontSize: '0.8125rem',
              px: 0.75,
              py: 0.25,
              borderRadius: '6px',
              bgcolor: 'action.hover',
              overflowWrap: 'anywhere',
            }}
          >
            {e.action}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.5 }}
          >
            {dateTime(e.occurredAt)} · {actor.label}
          </Typography>
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
          <Typography variant="body2" sx={{ fontWeight: 550 }}>
            {actor.label}
          </Typography>
          {actor.sub && (
            <Typography variant="caption" color="text.secondary">
              {actor.sub}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          {e.tenantId ? (
            <Link
              component={RouterLink}
              to={`/tenants/${e.tenantId}`}
              underline="hover"
              onClick={(ev) => ev.stopPropagation()}
            >
              {tenantName ?? shortId(e.tenantId)}
            </Link>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <StatusChip status={e.outcome} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box id={detailId} sx={{ py: 2, px: { xs: 0, sm: 1 } }}>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                  mb: 2,
                }}
              >
                <Field label="Actor">
                  {actor.label}
                  {actor.sub ? ` · ${actor.sub}` : ''}
                </Field>
                <Field label="Entity">
                  {e.entityType ?? '—'}
                  {e.entityId && (
                    <Box component="span" sx={{ fontFamily: mono, fontSize: 12, display: 'block' }}>
                      {e.entityId}
                    </Box>
                  )}
                </Field>
                <Field label="Request ID">
                  <Box component="span" sx={{ fontFamily: mono, fontSize: 12 }}>
                    {e.requestId ?? '—'}
                  </Box>
                </Field>
                <Field label="IP address">{e.ip ?? '—'}</Field>
                {e.tenantId && (
                  <Field label="Shop">
                    <Link component={RouterLink} to={`/tenants/${e.tenantId}`} underline="hover">
                      {tenantName ?? e.tenantId}
                    </Link>
                  </Field>
                )}
                {e.failureReason && <Field label="Failure reason">{e.failureReason}</Field>}
              </Box>
              {isBeforeAfter(changes) ? (
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1.5,
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" color="text.secondary">
                        Before
                      </Typography>
                      <JsonBlock value={changes.before} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" color="text.secondary">
                        After
                      </Typography>
                      <JsonBlock value={changes.after} />
                    </Box>
                  </Box>
                  {extra && Object.keys(extra).length > 0 && (
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        Also recorded
                      </Typography>
                      <JsonBlock value={extra} />
                    </Box>
                  )}
                </Stack>
              ) : changes !== null && changes !== undefined ? (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Details
                  </Typography>
                  <JsonBlock value={changes} />
                </Box>
              ) : null}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}
